import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Rating,
  CircularProgress,
  Link,
  Tooltip,
  } from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useAgentReport } from '../../../../contexts/AgentReportContext';

const BasicDetails = ({ agentId }) => {
  const { fetchSingleAgentDetails } = useAgentReport();
  
  // API data states
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationAddress, setLocationAddress] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Reverse geocoding function to get human-readable address with granular details
  const reverseGeocode = async (latitude, longitude) => {
    try {
      setLocationLoading(true);
      // Using OpenStreetMap Nominatim API (free, no API key required)
      // Try with different zoom levels to get more granular data
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&namedetails=1`,
        {
          headers: {
            'User-Agent': 'SalesGro-App' // Required by Nominatim
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('Nominatim full response:', data); // Debug log
        
        if (data.address || data.display_name) {
          const addr = data.address || {};
          const addressParts = [];
          
          // Strategy: Extract Area, City, State from available data
          // For Indian addresses, format is typically: "Area/Sector, City, State"
          
          // 1. Extract AREA (most granular location)
          // Try multiple fields that might contain area information
          const areaCandidates = [
            addr.quarter,
            addr.suburb,
            addr.neighbourhood,
            addr.residential,
            addr.locality,
            addr.sublocality,
            addr.sublocality_level_1,
            addr.sublocality_level_2,
            addr.city_district,
            // For Indian addresses, sometimes area is in district but not the same as city
            (addr.district && addr.district !== (addr.city || addr.town || addr.village)) ? addr.district : null
          ].filter(Boolean); // Remove null/undefined
          
          let areaFound = areaCandidates[0] || null;
          
          // If no area in address object, try parsing display_name
          if (!areaFound && data.display_name) {
            const displayParts = data.display_name.split(',').map(s => s.trim());
            // For Indian addresses: "Road/Area, City, State, Country"
            // Example: "Sector 63 Road, Sector 63, Noida, Uttar Pradesh, India"
            if (displayParts.length >= 3) {
              // Try index 0 and 1 - one might be road, one might be area
              // Check if it looks like an area (contains "Sector", numbers, or area-like names)
              for (let i = 0; i < Math.min(2, displayParts.length); i++) {
                const part = displayParts[i];
                // Check if it's likely an area (not a city)
                if (part && 
                    part.length > 0 && 
                    (part.match(/Sector|Phase|Block|Area|Colony|Nagari|Nagar/i) || 
                     part.match(/^\d+/) || // Starts with number
                     (part.length < 15 && !part.match(/Uttar Pradesh|Delhi|Maharashtra|Karnataka/i)))) {
                  areaFound = part;
                  break;
                }
              }
              // If still no area found, use first non-city part
              if (!areaFound && displayParts.length >= 3) {
                // City is usually one of the middle parts, so try first part
                areaFound = displayParts[0];
              }
            }
          }
          
          if (areaFound && areaFound.trim()) {
            addressParts.push(areaFound.trim());
          }
          
          // 2. Extract CITY
          const cityName = addr.city || addr.town || addr.village || addr.municipality;
          if (cityName && cityName.trim() && !addressParts.includes(cityName.trim())) {
            addressParts.push(cityName.trim());
          } else if (!cityName && data.display_name) {
            // Extract from display_name - city is usually 2nd or 3rd part
            const displayParts = data.display_name.split(',').map(s => s.trim());
            for (let i = 1; i < Math.min(3, displayParts.length); i++) {
              const part = displayParts[i];
              // City names are usually longer and don't contain numbers at start
              if (part && 
                  !part.match(/Uttar Pradesh|Delhi|Maharashtra|Karnataka|India|^\d+/) &&
                  !addressParts.includes(part)) {
                addressParts.push(part);
                break;
              }
            }
          }
          
          // 3. Extract STATE
          if (addr.state && addr.state.trim()) {
            addressParts.push(addr.state.trim());
          } else if (data.display_name) {
            const displayParts = data.display_name.split(',').map(s => s.trim());
            // State is usually 3rd or 4th part for Indian addresses
            for (let i = 2; i < Math.min(4, displayParts.length); i++) {
              const part = displayParts[i];
              if (part && 
                  (part.match(/Pradesh|Bengal|Tamil Nadu|Kerala|Gujarat|Rajasthan|Madhya Pradesh|Andhra Pradesh|Punjab|Haryana|Odisha|Bihar|Assam|Himachal Pradesh|Uttarakhand|Jharkhand|Chhattisgarh|Goa|Manipur|Meghalaya|Mizoram|Nagaland|Sikkim|Tripura|Arunachal Pradesh|Telangana/i) ||
                   part === 'Delhi' || part === 'Mumbai' || part === 'Kolkata')) {
                addressParts.push(part);
                break;
              }
            }
          }
          
          // Build final address: Area, City, State
          let formattedAddress;
          if (addressParts.length >= 2) {
            // Ensure we have at least area + city, or city + state
            formattedAddress = addressParts.join(', ');
          } else if (data.display_name) {
            // Fallback: Intelligent parsing of display_name
            const displayParts = data.display_name.split(',').map(s => s.trim());
            
            // Remove country (usually last part) and postal code if present
            const relevantParts = displayParts.filter((part, index) => {
              return index < displayParts.length - 1 && 
                     !part.match(/India|IN|\d{6}$/) && // Remove country and postal codes
                     part.length > 0;
            });
            
            // Take up to 3 parts: typically Area, City, State
            if (relevantParts.length >= 3) {
              // Skip first if it looks like a road name
              const startIndex = relevantParts[0].match(/Road|Street|Avenue|Lane|Nagari|Nagar$/i) ? 1 : 0;
              formattedAddress = relevantParts.slice(startIndex, startIndex + 3).join(', ');
            } else if (relevantParts.length >= 2) {
              formattedAddress = relevantParts.join(', ');
            } else {
              formattedAddress = relevantParts[0] || data.display_name;
            }
          } else {
            formattedAddress = `${latitude}, ${longitude}`;
          }
          
          console.log('Formatted address:', formattedAddress); // Debug log
          console.log('Address object:', addr); // Debug log
          setLocationAddress(formattedAddress);
        } else {
          setLocationAddress(`${latitude}, ${longitude}`);
        }
      } else {
        setLocationAddress(`${latitude}, ${longitude}`);
      }
    } catch (err) {
      console.error('Error reverse geocoding:', err);
      setLocationAddress(`${latitude}, ${longitude}`);
    } finally {
      setLocationLoading(false);
    }
  };

  // Fetch agent data on component mount
  useEffect(() => {
    if (!agentId) return;
    
    const fetchAgentData = async () => {
      console.log('BasicDetails - fetching agent data for:', agentId);
      setLoading(true);
      setError(null);
      
      try {
        const details = await fetchSingleAgentDetails(agentId);
        if (details) {
          setAgentData(details);
          console.log('BasicDetails - agent data fetched successfully');
          
          // If location is available as coordinates, fetch human-readable address
          if (details.location && typeof details.location === 'object' && details.location !== null) {
            const lat = parseFloat(details.location.latitude);
            const lng = parseFloat(details.location.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
              reverseGeocode(lat, lng);
            }
          }
        }
      } catch (err) {
        setError(err);
        console.error('Error fetching agent data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [agentId]);
  
  // Handle location click to open Google Maps
  const handleLocationClick = (latitude, longitude) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading agent data: {error.message}</Typography>
      </Box>
    );
  }

  // No agent data
  if (!agentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Agent not found</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{ 
            width: 60, 
            height: 60, 
            bgcolor: 'primary.main',
            fontSize: '1.5rem',
          }}
        >
          {agentData.name.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {agentData.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {agentData.email} • {agentData.phone} • Joined: {agentData.join_date || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {agentData.rating && agentData.rating !== '' && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating value={parseFloat(agentData.rating) || 0} readOnly size="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {agentData.rating} Rating
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Last Location:
              </Typography>
              {typeof agentData.location === 'object' && agentData.location !== null && 
               agentData.location.latitude && agentData.location.longitude ? (
                <Tooltip title={`Coordinates: ${agentData.location.latitude}, ${agentData.location.longitude}`}>
                  <Link
                    href={`https://www.google.com/maps?q=${agentData.location.latitude},${agentData.location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLocationClick(agentData.location.latitude, agentData.location.longitude);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.dark',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 16 }} />
                    {locationLoading ? (
                      <CircularProgress size={12} sx={{ ml: 0.5 }} />
                    ) : (
                      <span>
                        {locationAddress || `${agentData.location.latitude}, ${agentData.location.longitude}`}
                      </span>
                    )}
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {agentData.location || 'Not specified'}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Last Active: {agentData.last_active_at || 'Not specified'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                Status:
              </Typography>
              <Chip 
                label={agentData.status || 'Unknown'} 
                size="small" 
                color={agentData.status === 'Active' ? 'success' : 'default'}
              />
            </Box>
            {agentData.team && (
              <Typography variant="body2" color="text.secondary">
                Team: {agentData.team}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Team Rank: #{agentData.team_rank || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default BasicDetails;